import {
  PaymentAddress,
  PaymentCart,
  ShippingCalculationResult,
  ShippingOption,
  TaxCalculationResult,
} from './types';

const SHIPPING_URL = '/services_api/shipping/v1/calculation_requests.json';

const TAXES_URL = '/services_api/taxes/v1/calculation_requests.json';

/**
 * What the Shipping line reads when the order has no priced method of its own — see
 * `getShippingOptions`.
 */
const FREE_SHIPPING: ShippingOption = {
  id: 'free',
  name: 'Free Shipping',
  cost: 0,
  estimated_delivery: { minimum_number_of_days: 0, maximum_number_of_days: 0 },
};

/**
 * An amount of money, in the only denomination a payment sheet can quote it in: cents.
 *
 * The shipping api answers with a *rate*, and a rate is not a price. `USPS Expedited
 * (Insured)` comes back as 9.9468000000000001 — insurance is a fraction of what is in the
 * parcel, and nothing between the carrier and here has any reason to round it. So this is
 * where it becomes money, once, and everything below is written in terms of it.
 *
 * Rounded rather than truncated, because a half cent belongs to whichever side the
 * customer's own arithmetic would put it on.
 */
const toCents = (amount: number): number => Math.round(amount * 100) / 100;

/**
 * What a shipping address costs, and what the sheet should say it costs.
 *
 * Shared by both wallets and by nothing else yet. It exists because the two payment
 * sheets ask the same three questions in the same order — what does shipping cost here,
 * what is the tax on that, what does the whole thing come to — and answering them twice
 * is how the two buttons end up quoting different totals for the same order.
 *
 * The two endpoints are jshop's own and are addressed relatively, the way
 * `cart/addItem` addresses the cart api. headless reached the same two through
 * NEXT_PUBLIC_SHIPPING_API_BASE_URL / NEXT_PUBLIC_TAXES_API_BASE_URL, which pointed
 * back here; from inside the monolith there is nothing to point at.
 *
 * Neither call is wrapped in a try/catch. A failure has to reach the caller, because
 * only the caller knows how to tell *its* sheet that the address was no good —
 * completeShippingContactSelection with an error status for Apple, a
 * SHIPPING_ADDRESS_INVALID update for Google. Swallowing it here would leave the sheet
 * quoting a total that never included shipping.
 */
export default function usePayment() {
  /**
   * Every amount a sheet prints, and the only way one is written.
   *
   * Both sheets take their money as strings and each used to reach for `toFixed(2)` at the
   * point it needed one — except Google's shipping method list, which interpolated the
   * number itself and so offered the customer "9.9468000000000001 USD: USPS Expedited
   * (Insured)". Apple's list was fine only by accident: it is handed the estimate alone and
   * prints the amount in a column of its own.
   */
  const formatAmount = (amount: number): string => toCents(amount).toFixed(2);

  /**
   * The number on the sheet's Total line. Discounts are already subtracted from
   * nothing else, so this is the only place the arithmetic happens.
   *
   * Added as cents rather than as the numbers came in, so that the Total is the sum of the
   * lines printed above it. A rate of 9.9468 that prints as 9.95 and totals as 9.9468 is a
   * penny the customer can see and cannot account for.
   */
  const calculateCartTotal = (cart: PaymentCart): number =>
    toCents(cart.total_items_price) -
    toCents(cart.total_discounts) +
    toCents(cart.shipping_total_amount) +
    toCents(cart.tax_total_amount);

  const toRequestItems = (cart: PaymentCart, address: PaymentAddress) =>
    cart.items.map((item) => ({
      product_id: item.product.id,
      quantity: item.quantity,
      // What is actually being charged for the line. The apis take one number, so the
      // discount is applied here rather than sent alongside — tax on the pre-discount
      // price would be tax on money nobody paid.
      price: item.price - item.discount,
      delivery_address: toRequestAddress(address),
    }));

  /**
   * The apis want a whole address; a wallet has only ever collected the part that
   * decides the rate. The empty strings are what headless sent too.
   */
  const toRequestAddress = (address: PaymentAddress) => ({
    name: { first_name: '', last_name: '' },
    address1: '',
    address2: '',
    city: address.city,
    state: address.state,
    zip: address.zip,
    country_iso2: address.country_iso2,
  });

  const post = async <T>(url: string, body: unknown): Promise<T> => {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`${url} failed with ${response.status}`);
    }

    return (await response.json()) as T;
  };

  /**
   * Prices the delivery and picks a method — the one the visitor asked for by id, or
   * the cheapest-first option the api put at the top when they have not chosen yet.
   */
  const calculateShipping = async (
    cart: PaymentCart,
    address: PaymentAddress,
    shippingOptionId?: string | null
  ): Promise<PaymentCart> => {
    const result = await post<ShippingCalculationResult>(SHIPPING_URL, {
      billing_address: null,
      items: toRequestItems(cart, address),
    });

    const selectedShippingOption =
      result.shipping_options.find((option) => option.id === shippingOptionId) || result.shipping_options[0];

    return {
      ...cart,
      address,
      shipping_total_amount: selectedShippingOption?.cost ?? result.total_cost,
      selected_shipping_option: selectedShippingOption,
      shipping_calculation_result: result,
    };
  };

  /**
   * Tax depends on what shipping was chosen, so this runs after calculateShipping and
   * is handed its result — the api charges tax on delivery in some states.
   */
  const calculateTaxes = async (cart: PaymentCart, address: PaymentAddress): Promise<PaymentCart> => {
    const result = await post<TaxCalculationResult>(TAXES_URL, {
      shipping_calculation_result: cart.shipping_calculation_result,
      shipping_option_id: cart.selected_shipping_option?.id ?? null,
      billing_address: toRequestAddress(address),
      items: toRequestItems(cart, address),
    });

    return { ...cart, tax_total_amount: result.total };
  };

  /**
   * The methods there is a genuine choice between — the ones the api priced, and nothing
   * else. Empty is a real and ordinary answer.
   *
   * `ShippingCalculator::buildRequestItem()` builds a request line only for a book or a
   * miscellaneous good, so a cart of magazine subscriptions produces no request and the api
   * answers 200 with `shipping_options: []` and a `total_cost` of 0 — which is true:
   * nothing is being posted. That is the ordinary case on this site, and it is the whole
   * difference between the two placements: the book product page sells the one type that
   * ships, the sale cart sells the type that does not.
   *
   * A sheet that can present a chooser should be given this and allowed to present nothing.
   */
  const getSelectableShippingOptions = (cart: PaymentCart): ShippingOption[] =>
    cart.shipping_calculation_result?.shipping_options ?? [];

  /**
   * The same methods, but never empty — for a sheet that has to be told a shipping cost
   * whether or not one was priced, and for the Shipping line either sheet prints.
   *
   * Google's is the first: `shippingOptionParameters` requires at least one option, and its
   * callback reads `.name` and `.id` off the selected one. So an order that does not ship
   * is quoted the free placeholder, which is what headless showed before the first address
   * and is also what a subscription is genuinely charged for delivery.
   *
   * It stays presentational. `calculateShipping` still records no `selected_shipping_option`
   * when the api offered none, so the payload posts `shippingOptionId: null` rather than an
   * id checkout has never issued.
   */
  const getShippingOptions = (cart: PaymentCart): ShippingOption[] => {
    const selectable = getSelectableShippingOptions(cart);

    return selectable.length ? selectable : [FREE_SHIPPING];
  };

  const getSelectedShippingOption = (cart: PaymentCart): ShippingOption => {
    const shippingOptions = getShippingOptions(cart);

    return shippingOptions.find((option) => option.id === cart.selected_shipping_option?.id) || shippingOptions[0];
  };

  /**
   * The line under a shipping method's name. Apple's sheet has its own price column and
   * wants the estimate alone; Google's does not, and wants the price spelled out.
   */
  const getShippingOptionDescription = (option: ShippingOption, estimatedDeliveryOnly = false): string => {
    if (option.estimated_delivery.minimum_number_of_days === 0) {
      return option.name;
    }

    const days = `${option.estimated_delivery.minimum_number_of_days}-${option.estimated_delivery.maximum_number_of_days} days`;

    return estimatedDeliveryOnly ? days : `${formatAmount(option.cost)} USD: ${option.name} (${days})`;
  };

  return {
    calculateShipping,
    calculateTaxes,
    calculateCartTotal,
    formatAmount,
    getSelectableShippingOptions,
    getShippingOptions,
    getSelectedShippingOption,
    getShippingOptionDescription,
  };
}
